import { load as loadProto } from 'protobufjs';

import errorReporting from 'APP_ROOT/services/errorReporting';
import Events from 'APP_ROOT/components/common/events';
import RavenUtil from 'APP_ROOT/components/common/ravenUtil';
import decodeLL from 'APP_ROOT/components/common/gpsDecoder';
import NotificationService from 'APP_ROOT/components/common/notificationService';

import { updateMedia } from 'APP_ROOT/data/media/actions';
import { updateEventAndDependentMedia } from 'APP_ROOT/data/events/actions';

import { makeClient } from 'API_FILE';

var currentlySubscribedTopics = [];
var proto_data, p_event_type, p_sensor_type, proto_CTRLRequest, proto_CTRLResponse;

const VALUE_KEYS = ['stringValue', 'intValue', 'floatValue', 'boolValue'];
const unwrapValue = value => {
    const key = VALUE_KEYS.find(k => value.hasOwnProperty(k));
    return value[key];
}

export const DEFAULT_PROTOBUF_PROCESS_INTERVAL = 2000;

//must be initialized after being created
export default class MqttService {

    constructor(sessionManager, userid, dispatch, initialDFastState, userProtobufProcessInterval = DEFAULT_PROTOBUF_PROCESS_INTERVAL) {

        // FIXME get rid of the ref to sessionManager
        this.sessionManager = sessionManager;
        this.ravenUuids = [];
        this.sessionManager.state.ravens.forEach(raven => {
            // Don't add no service plan ravens to the MQTT listen list
            if (raven.noServicePlan)
                return;
            if (raven._noMqtt) return;
            this.ravenUuids.push({
                uuid: raven.ravenUuid,
                lastdfast: Date.now()
            })
        });

        var pieces = userid.split(':');
        this.userid = pieces[1];

        this.protobufsReady = false;
        this.sendWhenReady = false;
        this.loadProtobufs();
        this.notificationService = new NotificationService(this.sessionManager.state.ravens);
        this.ctrlRequestsInProgress = [];

        this.protobufQueue = [];
        this.protobufNextTick = Date.now();
        this.protobufTimeout = null;
        this.protobufProcessInterval = userProtobufProcessInterval;

        this.dfastEnabled = initialDFastState;
        this.dfastPaused = false;

        this.setDfastInterval();
        // this.dfastInterval = 15000;

        this.dfastTimeout = null;

        document.addEventListener(Events.PAUSE_DFAST, () => this.pauseDfast(true));
        document.addEventListener(Events.UNPAUSE_DFAST, () => { this.pauseDfast(false); this.sendAllRavensDfast(); });

        // the ravenUuid to send ctrl messages to in showcase stage
        this.showcaseTargetRavenUuid = undefined;

        // dispatch function for the redux store
        this.dispatch = dispatch;

        this.dfastWasSent = false;

        this.visibleRavenUuids = new Set();
    }

    setDfastInterval() {
        if (this.isSnapshotPage()) {
            this.dfastInterval = 66000;
            this.dfastMode = 'snapshot';
        } else {
            this.dfastInterval = 33000;
            this.dfastMode = 'dashboard';
        }
        console.log(`[DFAST] setDfastInterval: mode=${this.dfastMode}, pathname=${window.location.pathname}`);

        // Cancel any pending dfast timeout from a previous page's round-robin
        if (this.dfastTimeout) {
            clearTimeout(this.dfastTimeout);
            this.dfastTimeout = null;
        }
        // Immediately re-send dfasts with the correct frequency for the new page
        if (this.dfastEnabled && !this.dfastPaused && this.mqttClient) {
            this.sendAllRavensDfast();
        }
    }


    subscribe(topic) {
        if (this.mqttClient) {
            this.mqttClient.subscribe(topic);
        }
        // add to currentlySubscribedTopics if not already present
        if (!currentlySubscribedTopics.some(t => t === topic)) {

            currentlySubscribedTopics.push(topic);
        }
    }

    unsubscribe(topic) {
        if (this.mqttClient) {
            this.mqttClient.unsubscribe(topic);
        }
        currentlySubscribedTopics = currentlySubscribedTopics.filter(t => t !== topic);
    }

    setShowcaseTargetRavenUuid(ravenUuid) {
        if (this.showcaseTargetRavenUuid === ravenUuid) return;
        // if subscribed, unsubscribe
        if (this.showcaseTargetRavenUuid) {
            const topicsToUnsubscribe = currentlySubscribedTopics.filter(t => t.includes(this.showcaseTargetRavenUuid));
            topicsToUnsubscribe.forEach(t => {
                this.unsubscribe(t);
            });
        }
        this.showcaseTargetRavenUuid = ravenUuid;
        const stage = this.sessionManager.state.user.stage;
        // subscribe to ctrl/response if connected
        // FIXME Showcase - old style topic
        this.subscribe(`things/${ravenUuid}/${stage}/ctrl/response`);
    }

    unmount() {
        if (this.mqttClient) {
            this.ending = true;
            currentlySubscribedTopics.forEach((topic) => {
                this.mqttClient.unsubscribe(topic);
            });
            this.mqttClient.end(true);
            this.mqttClient = null;

            window.removeEventListener('focus', this.onWindowFocus);
            if (this.listeningOnBlur)
                window.removeEventListener('blur', this.onWindowBlur);
        }
    }

    onVisibilityChange = (e) => {

        // console.log('window visibilitychange', e, document.visibilityState);
        if (document.visibilityState === 'visible') {
            this.onWindowFocus(e);
        } else if (document.visibilityState === 'hidden') {
            this.onWindowBlur(e);
        }
    }

    onWindowFocus = (e) => {

        this.pauseDfast(false);
    }

    onWindowBlur = (e) => {

        this.pauseDfast(true);
    }

    enableDfast(isEnabled = true) {
        this.dfastEnabled = isEnabled;
    }

    pauseDfast(isPaused = true) {
        this.dfastPaused = isPaused;
        if (!isPaused) {
            this.ending = false;
            this.resetInteractionTimer();
            if (!this.mqttClient) {
                try {
                    this.init();
                } catch (e) {
                    console.error(e);
                }
            }
        }
    }

    loadProtobufs() {
        loadProto("/raven-protocol.json").then(root => {
            proto_data = root.lookupType("klashwerks.raven.Data");
            p_event_type = root.lookupEnum('EventType').valuesById;
            p_sensor_type = root.lookupEnum('SensorType').valuesById;
            proto_CTRLRequest = root.lookupType("klashwerks.raven.CTRLRequest");
            proto_CTRLResponse = root.lookupType("klashwerks.raven.CTRLResponse");
            this.mediaTypeEnum = root.lookupEnum('MediaType');
            this.cameraTypeEnum = root.lookupEnum('CameraType');
            this.eventTypeEnum = root.lookupEnum('EventType');
            this.eventParamKeyEnum = root.lookupEnum('EventParamKey');
            this.protobufsReady = true;
            this.init()
        }).catch(err => console.error(err));
    }

    init() {

        if (this.ravenUuids.length) {
            this.mqttClient = makeClient(this.sessionManager.state.user.email);

            // Subscribe to all Ravens under this uuid

            currentlySubscribedTopics.push('things/' + this.userid + '/+/' + this.sessionManager.state.user.stage + '/app');
            currentlySubscribedTopics.push('things/' + this.userid + '/+/' + this.sessionManager.state.user.stage + '/ctrl/response');

            //
            // Install connect/reconnect event handlers.
            //
            this.mqttClient.on('subscribe', (e) => { console.log(e) });
            this.mqttClient.on('connect', this.mqttClientConnectHandler);
            this.mqttClient.on('reconnect', this.mqttClientReconnectHandler);
            this.mqttClient.on('message', this.mqttClientMessageHandler);
            this.mqttClient.on('timeout', this.mqttClientTimeoutHandler);
            this.mqttClient.on('error', (error) => {
                // if not handled, every unexpected disconnect will be reported to Sentry
                console.log(error)
            });

            window.addEventListener('blur', this.onWindowBlur);
            window.addEventListener('focus', this.onWindowFocus);
            window.addEventListener('visibilitychange', this.onVisibilityChange);

            window.addEventListener('click', this.unPause);
            window.addEventListener('keydown', this.unPause);

            this.listeningOnBlur = true;

        } else {
            console.error('Please pass a ravenUuid to connect the MQTT service to.');
        }
    }

    startInteractionTimeout() {
        if (this.inactivityTimeoutDisabled) return;

        this.interactionTimeout = setTimeout(() => {

            document.dispatchEvent(new CustomEvent(Events.PAUSE_DFAST));
        }, 118000) //2 minutes minus 2 seconds to prevent requesting one too many dfasts - beats this.dfastinterval from reaching another 30s timeout
    }

    nextCtrlId() {
        if (!this.lastCtrlId) {
            this.lastCtrlId = Date.now() & 0xFFFFFFFF;
        } else if (this.lastCtrlId === 0xFFFFFFFF) {
            this.lastCtrlId = 1;
        } else {
            this.lastCtrlId += 1;
        }
        return this.lastCtrlId;
    }

    unPause = (e) => {

        document.dispatchEvent(new CustomEvent(Events.UNPAUSE_DFAST));
    }

    disableInactivityTimeout() {
        this.inactivityTimeoutDisabled = true;
        if (this.interactionTimeout) {
            clearTimeout(this.interactionTimeout);
            this.interactionTimeout = null;
        }
    }

    enableInactivityTimeout() {
        this.inactivityTimeoutDisabled = false;
        this.resetInteractionTimer();
    }

    resetInteractionTimer = (e) => {
        if (this.inactivityTimeoutDisabled) return;

        // console.log("RESET dfast interaction timer");
        this.dfastPaused = false;
        if (this.interactionTimeout) clearTimeout(this.interactionTimeout);
        this.startInteractionTimeout();
    }

    applyProtobufProcessInterval = (userProtobufProcessInterval) => {

        if (!userProtobufProcessInterval) { // zero, null, undefined, empty string
            return;
        }

        if (userProtobufProcessInterval === this.protobufProcessInterval) {
            return;
        }

        this.protobufProcessInterval = userProtobufProcessInterval;

        if (this.protobufTimeout) {
            this.protobufTimeout = setTimeout(this.queueAndProcessProtobuf, this.protobufProcessInterval);
        }
    }

    mqttClientConnectHandler = () => {
        if (!this.mqttClient) return;
        currentlySubscribedTopics.forEach((topic) => {
            this.mqttClient.subscribe(topic);
        });
        this.onMqttConnectionChanged(true);
    }

    mqttClientReconnectHandler = () => {
        if (this.dfastPaused) {
            this.unmount();
            return;
        }
        console.log('MQTTService reconnect');
        const {
            accessKeyId,
            secretAccessKey,
            sessionToken
        } = window.AWS.config.credentials;
        if (this.mqttClient) {
            this.mqttClient.updateWebSocketCredentials(
                accessKeyId,
                secretAccessKey,
                sessionToken,
            );
        }
    }

    //
    // Message handler for lifecycle events; create/destroy divs as clients
    // connect/disconnect.
    //
    mqttClientMessageHandler = (topic, payload) => {

        // console.log('MQTTService message from topic: ', topic);

        try {
            var topicTokens = topic.split("/");
            if (topicTokens[0] !== "things") {
                // FIXME - what do we do if we get an unknown topic?
                return;
            }
            var userId = topicTokens[1];
            var ravenUuid = topicTokens[2];
            if (topic.indexOf('/ctrl/response') >= 0) {
                payload = proto_CTRLResponse.decode(payload);
                // console.log('/response payload: ', payload);
                const request = this.removeCtrlRequestInProgress(payload.ctrlId);
                if (request) {
                    // console.log("resolving request", request);
                    // console.log(payload.queryResponse);
                    request.resolve(payload);
                } else if (payload.queryResponse) {
                    // console.log("no request in progress for " + payload.ctrlId);
                    // console.log(payload.queryResponse);
                    // console.log(this.ctrlRequestsInProgress);
                    this.onMQTTConfigResponse(ravenUuid, payload.queryResponse.configurationResponse);
                }
            } else if (topic.indexOf('/ctrl') >= 0) {
                payload = proto_CTRLRequest.decode(payload);
                // console.log('/request payload: ', payload);
            } else {
                payload = proto_data.decode(payload);
                this.queueAndProcessProtobuf({ ravenUuid: ravenUuid, payload: payload });
            }
        } catch (e) {
            errorReporting.logw({ category: 'mqtt', message: 'protobuf could not be decoded', data: { payload: this.arrayBufferToBase64(payload), topic: topic } });
            errorReporting.error(e);
        }
    }

    queueAndProcessProtobuf = (inpay) => {

        if (inpay) {
            this.protobufQueue.push(inpay);
            if (!this.protobufTimeout)
                this.protobufTimeout = setTimeout(this.queueAndProcessProtobuf, this.protobufProcessInterval);
        }

        if (this.protobufNextTick > Date.now())
            return;

        // console.log("/data payload: ", payload);
        var updateRavens = false;
        var numprocessed = 0;
        while (true) {
            if (this.protobufQueue.length <= 0)
                break;

            var data = this.protobufQueue.shift();
            var payload = data.payload;
            var ravenUuid = data.ravenUuid;

            numprocessed++;

            //image/MEDIA
            if (payload.media) {
                // dispatch to the redux store
                payload.media.forEach(({ mediaId, timestamp, imageData, contentType, type, cameraType, url, ...rest }) => {
                    // imageData, contentType removed by omission from `rest`
                    this.dispatch(updateMedia(ravenUuid,
                        {
                            ...rest,
                            key: url,
                            mediaId: `${mediaId}`,
                            cameraType: this.cameraTypeEnum.valuesById[cameraType],
                            type: this.mediaTypeEnum.valuesById[type],
                            timestamp: RavenUtil.toDate(timestamp)
                        }
                    ));
                });

                if (this.onMQTTLiveImage(ravenUuid, payload.media)) updateRavens = true;
            }
            // Process payload

            // events
            if (payload.event && payload.event.length > 0) {
                // dispatch to the redux store
                const mqttService = this;
                payload.event.forEach(({ type, linkedEventId, timestamp, mediaId, gps, param, ...it }) => {

                    this.dispatch(updateEventAndDependentMedia(ravenUuid,
                        {
                            ...it,
                            type: p_event_type[type],
                            timestamp: RavenUtil.toDate(timestamp),
                            mediaIds: mediaId.map(id => `${id}`),
                            geoStamp: gps ? {
                                lon: decodeLL(gps.longitude.value),
                                lat: decodeLL(gps.latitude.value),
                            } : null,
                            linkedEventId: linkedEventId ? {
                                timestamp: RavenUtil.toDate(linkedEventId.timestamp),
                                type: p_event_type[linkedEventId.type]
                            } : null,
                            eventParams: param && param.reduce((acc, it) => {
                                const key = this.eventParamKeyEnum.valuesById[it.key];
                                acc[key] = unwrapValue(it.value);
                                return acc;
                            }, {})
                        },
                        mqttService
                    )).then(event => {
                        if (event.type === 'EVENT_MEDIA_UPLOAD_COMPLETED' && event.mediaIds[0]) {
                            document.dispatchEvent(Events.refreshMedia(event.mediaIds[0], event.type));
                        }

                        // historical streaming events need to trigger a card refresh in history
                        if (event.type === 'EVENT_KLOUD_HISTORICAL_STREAMING' && event.mediaIds[0]) {
                            document.dispatchEvent(Events.refreshMedia(event.mediaIds[0], event.type));
                        }

                        // user video request should be treated same as historical streaming events, need to trigger a card refresh in history
                        if (event.type === 'EVENT_CUSTOM' && event.mediaIds[0]) {
                            document.dispatchEvent(Events.refreshMedia(event.mediaIds[0], event.type));
                        }

                        // if event is EVENT_KLOUD_DRIVER_DETECTED, refresh all ravens
                        if (event.type === 'EVENT_KLOUD_DRIVER_DETECTED') {
                            document.dispatchEvent(Events.refreshAllRavens());
                        }
                        // if playing a looping demo, EVENT_DEMO_LOOP_STARTED indicates the loop started again
                        // and we need to refresh everything
                        if (event.type === 'EVENT_DEMO_LOOP_STARTED') {
                            document.dispatchEvent(Events.refreshAllRavens());
                            document.dispatchEvent(new CustomEvent(Events.REFRESH_RAVEN));
                        }
                        /* FIXME - "refreshAllRavens" is overkill for a DTC event, and it doesn't work
                           Should narrow down cards query (all ravens) but only refresh the dashboard for <ravenUuid>
                        if(event.type === 'EVENT_OBD_DTC_ALERT' ||
                            event.type === 'EVENT_OBD_DTC_CLEARED') {
                            document.dispatchEvent(Events.refreshAllRavens());
                        }*/
                    });
                });

                payload.event.forEach(elem => {
                    var decodedEvent = this.decodeEvent(elem);
                    if (!elem.type) return;

                    switch (decodedEvent.type) {
                        case 'EVENT_STORAGE_SD_CARD_INSERTED':
                        case 'EVENT_STORAGE_SD_CARD_MOUNTED': // RAV-993
                            this.onMQTTSDCardStatusUpdate(ravenUuid, "INSERTED"); // using cloud type "INSERTED"
                            updateRavens = true;
                            break;
                        case 'EVENT_STORAGE_SD_CARD_REMOVED':
                        case 'EVENT_STORAGE_SD_CARD_NOT_INSERTED': // RAV-993
                            this.onMQTTSDCardStatusUpdate(ravenUuid, "REMOVED"); // using cloud type "REMOVED"
                            updateRavens = true;
                            break;
                        case 'EVENT_STORAGE_SD_CARD_ERROR':
                            this.onMQTTSDCardStatusUpdate(ravenUuid, "ERROR"); // using cloud type "ERROR"
                            updateRavens = true;
                            break;
                        case 'EVENT_STORAGE_SD_CARD_NEEDS_FORMAT':
                            this.onMQTTSDCardStatusUpdate(ravenUuid, "NEEDS_FORMAT"); // using cloud type "NEEDS_FORMAT"
                            updateRavens = true;
                            break;
                        case 'EVENT_STORAGE_SD_CARD_FULL':
                            this.onMQTTSDCardStatusUpdate(ravenUuid, "CARD_FULL"); // using cloud type "CARD_FULL"
                            updateRavens = true;
                            break;
                        case 'EVENT_STORAGE_SD_CARD_TOO_SMALL':
                            this.onMQTTSDCardStatusUpdate(ravenUuid, "CARD_TOO_SMALL"); // using cloud type "CARD_TOO_SMALL"
                            updateRavens = true;
                            break;
                        case 'EVENT_RAVEN_UNPLUGGED':
                            this.onMQTTRavenUnplugged(ravenUuid, true);
                            updateRavens = true;
                            break;
                        case 'EVENT_RAVEN_PLUGGED_IN':
                            this.onMQTTRavenUnplugged(ravenUuid, false);
                            updateRavens = true;
                            break;
                        case 'EVENT_IDLING_STARTED':
                            this.onMQTTRavenIdlingUpdate(ravenUuid, true);
                            updateRavens = true;
                            break;
                        case 'EVENT_IDLING_STOPPED':
                            this.onMQTTRavenIdlingUpdate(ravenUuid, false);
                            updateRavens = true;
                            break;
                        case 'EVENT_OBD_DTC_ALERT': // requires vehicle state updated, see also redux store dispatch promise above
                        case 'EVENT_OBD_DTC_CLEARED': // requires vehicle state updated, see also redux store dispatch promise above
                            document.dispatchEvent(Events.refreshAllRavens());
                            break;
                        case 'EVENT_RAVEN_SHUTDOWN':
                        case 'EVENT_RAVEN_USER_SHUTDOWN':
                        case 'EVENT_ENGINE_ON':
                        case 'EVENT_ENGINE_OFF':
                        case 'EVENT_SPEEDING_STARTED':
                        case 'EVENT_SPEEDING_STOPPED':
                        case 'EVENT_POSSIBLE_IMPACT':
                        case 'EVENT_RAVEN_BAD_INSTALL_DETECTED':
                        case 'EVENT_RAVEN_BAD_INSTALL_CLEARED':
                            updateRavens = true;
                            break;
                        default:
                            break;
                    }

                    decodedEvent = document.filterEvent(decodedEvent);
                    if (!decodedEvent) return;
                    if (this.onMQTTEvent(ravenUuid, decodedEvent)) updateRavens = true;
                });
            }
            //telematics
            if (payload.telematics && payload.telematics.length > 0 && payload.telematics) {
                var routeUpdate = [];
                var currentFuel = null;
                var currentSpeed = null;
                var timestamp = null;
                payload.telematics.forEach((elem, index) => {
                    elem.timestamp *= 1000; //convert to standard UTC time
                    if (elem.gps) {
                        routeUpdate.push({
                            lon: decodeLL(elem.gps.longitude.value),
                            lat: decodeLL(elem.gps.latitude.value),
                            ts: RavenUtil.toDate(elem.timestamp)
                        });
                    }
                    if (elem.sensor) {
                        elem.sensor.forEach((sensor, index) => {
                            if (p_sensor_type[sensor.type] === "SENSOR_OBD_FUEL_LEVEL") {
                                currentFuel = sensor.mean;
                            }
                            if (p_sensor_type[sensor.type] === "SENSOR_OBD_SPEED") {
                                currentSpeed = sensor.mean;
                            }
                            timestamp = elem.timestamp;
                        });
                    }
                });

                // Only call update once per telematics packet
                if (routeUpdate.length > 0)
                    if (this.onMQTTRouteUpdate(ravenUuid, routeUpdate)) updateRavens = true;

                if ((currentFuel || currentSpeed) && timestamp) {
                    if (this.onMQTTTelematicsUpdate(ravenUuid, currentFuel, currentSpeed, timestamp)) updateRavens = true;
                }
            }
        }

        this.protobufNextTick = Date.now() + this.protobufProcessInterval;

        // Send update message for all processed packets
        if (updateRavens)
            this.sessionManager.updateRavens(this.sessionManager.state.ravens);
    }

    //
    // Handle timeout when trying any call
    //
    mqttClientTimeoutHandler = (topic, clientToken) => {

        // console.log('MQTTService message: ' + topic + ':' + clientToken.toString());
    }

    decodeEvent(event) {

        const { gps, param } = event;

        var decodedEvent = {};
        decodedEvent['duration'] = 0;
        if (event.duration) {
            decodedEvent['duration'] = event.duration;
        }

        decodedEvent.geoStamp = gps ? {
            lon: decodeLL(gps.longitude.value),
            lat: decodeLL(gps.latitude.value),
        } : null;

        if (event.mediaId) {
            decodedEvent['media'] = [];
            if (event.mediaId.length > 2) event.mediaId.splice(0, 2);
            event.mediaId.forEach((elem, index) => {
                decodedEvent['media'].push({
                    mediaId: elem,
                    type: "MEDIA_PREVIEW",
                    cameraType: index === 0 ? "CAMERA_ROAD" : "CAMERA_CABIN",
                    ts: event.timestamp
                });
            });
        }
        decodedEvent['ts'] = event.timestamp;
        decodedEvent['type'] = p_event_type[event.type];

        decodedEvent.eventParams = param && param.reduce((acc, it) => {
            const key = this.eventParamKeyEnum.valuesById[it.key];
            acc[key] = unwrapValue(it.value);
            return acc;
        }, {});

        return decodedEvent;
    }

    isSnapshotPage = () => {
        let pathname = window.location.pathname; // e.g. "/en/dashboard"
        return pathname.includes('/snapshot');
    }

    DfastSentFlagCancel = () => {
        if (this.dfastWasSent) {
            this.dfastWasSent = false;
        }
    }

    sendDfast = (targetRavenUuid, requestPreviews = true) => {
        if (this.ending || this.dfastPaused || document.visibilityState !== 'visible') return;
        if (!this.mqttClient) {
            try {
                this.init();
            } catch (e) {
                console.error(e);
            }
            return;
        }
        if (!this.protobufsReady) {
            this.sendWhenReady = true;
            return;
        }
        if (!targetRavenUuid) {
            console.error("tried to send dfast to a raven with no ravenUuid - something broke bad");
            return;
        }

        if (this.dfastMode === 'snapshot') {
            var payload = {
                ctrlId: this.nextCtrlId(),
                configurationRequest: {
                    frequencyConfiguration: {
                        action: 1,
                        updateFrequency: [
                            {
                                type: 2,
                                frequency: 60
                            },
                            {
                                type: 3,
                                frequency: 60
                            },
                            {
                                type: 6,
                                frequency: 60
                            },
                            {
                                type: 7,
                                frequency: 60
                            }
                        ]
                    }
                }
            };

            if (!requestPreviews) {
                payload = {
                    ctrlId: this.nextCtrlId(),
                    configurationRequest: {
                        frequencyConfiguration: {
                            action: 1,
                            updateFrequency: [
                                {
                                    type: 2,
                                    frequency: 60
                                },
                                {
                                    type: 3,
                                    frequency: 60
                                }
                            ]
                        }
                    }
                };
            }
        }
        else {
            payload = {
                ctrlId: this.nextCtrlId(),
                configurationRequest: {
                    frequencyConfiguration: {
                        action: 1,
                        updateFrequency: [
                            {
                                type: 2,
                                frequency: 2
                            },
                            {
                                type: 3,
                                frequency: 15
                            },
                            {
                                type: 6,
                                frequency: 2
                            },
                            {
                                type: 7,
                                frequency: 15
                            }
                        ]
                    }
                }
            };

            if (!requestPreviews) {
                payload = {
                    ctrlId: this.nextCtrlId(),
                    configurationRequest: {
                        frequencyConfiguration: {
                            action: 1,
                            updateFrequency: [
                                {
                                    type: 2,
                                    frequency: 2
                                },
                                {
                                    type: 3,
                                    frequency: 15
                                }
                            ]
                        }
                    }
                };

            }
        }

        // console.log(`[DFAST UPDATE] Sending to Raven: ${targetRavenUuid} (Previews: ${requestPreviews}, Mode: ${this.dfastMode}), Payload: ${payload}`);

        var err = proto_CTRLRequest.verify(payload);
        if (err) throw Error(err);
        // Create a new message
        var message = proto_CTRLRequest.create(payload); // or use .fromObject if conversion is necessary
        // Encode a message to an Uint8Array (browser) or Buffer (node)
        var buffer = proto_CTRLRequest.encode(message).finish();
        //only send dfast to one raven now. The selected one.
        // this.mqttClient.publish('things/' + targetRavenUuid + '/' + this.sessionManager.state.user.stage + '/ctrl', buffer);

        const usersRavenCtrlTopic = this.ctrlTopic(targetRavenUuid);
        if (!usersRavenCtrlTopic) {
            console.warn("MqttService sendDfast user ctrlTopic for raven is undefined.");
            return;
        }

        var me = this;

        this.mqttClient.publish(usersRavenCtrlTopic, buffer, {}, (err) => {
            if (err) {
                // FIXME - We should handle this better?
                console.log("MQTT ERROR", err)
            }
            me.dfastCallback();
        });
        // console.log('MQTTService DFAST sent.');

        this.sessionManager.updatesDelayed(false);

        this.dfastWasSent = true;
    }

    sendAllRavensDfast = () => {
        if (this.dfastMode === 'snapshot') {
            // On snapshot page, send dfast to ALL ravens at once so they all
            // immediately get the 60s frequency (no round-robin delay)
            this.sendSnapshotDfastToAll();
            return;
        }

        // Dashboard: prioritize the selected raven, then round-robin
        var selRaven = this.sessionManager.state.selectedRaven;

        if (selRaven) {
            for (var i = 0; i < this.ravenUuids.length; i++) {
                if (this.ravenUuids[i].uuid === selRaven.ravenUuid) {
                    var curravenarray = this.ravenUuids.splice(i, 1);
                    var curraven = curravenarray[0];
                    curraven.lastdfast -= this.dfastInterval;
                    this.ravenUuids.unshift(curraven);
                    break;
                }
            }
        }

        this.sendNextDFastInQueue();
    }

    sendSnapshotDfastToAll = () => {
        if (this.ending || this.dfastPaused || document.visibilityState !== 'visible') return;

        console.log(`[DFAST] sendSnapshotDfastToAll: sending 60s to all ${this.ravenUuids.length} ravens`);

        // Send dfast to every raven at once with 60s frequency
        this.ravenUuids.forEach(raven => {
            const isOnCurrentPage = this.visibleRavenUuids.has(raven.uuid);
            this.sendDfast(raven.uuid, isOnCurrentPage);
            raven.lastdfast = Date.now();
        });

        // Re-send every 55s to keep devices on the 60s frequency
        if (this.dfastTimeout) clearTimeout(this.dfastTimeout);
        this.dfastTimeout = setTimeout(this.sendSnapshotDfastToAll, 55000);
    }

    dfastCallback = () => {
        if (this.dfastEnabled && !this.dfastPaused && document.visibilityState === 'visible') {

            // On snapshot page, scheduling is handled by sendSnapshotDfastToAll
            if (this.dfastMode === 'snapshot') return;

            var timedelta = this.ravenUuids[0].lastdfast + this.dfastInterval - Date.now();

            if (timedelta > 0) {
                if (this.dfastTimeout) clearTimeout(this.dfastTimeout);
                this.dfastTimeout = setTimeout(this.sendNextDFastInQueue, timedelta);
            }
            else {
                this.sendNextDFastInQueue();
            }
        }
    }

    setVisibleRavens(uuids) {
        console.log('[MQTT] Setting visible ravens for preview:', uuids);
        this.visibleRavenUuids = new Set(uuids);
    }

    sendNextDFastInQueue = () => {
        var nextRaven = this.ravenUuids.shift();
        var selRaven = this.sessionManager.state.selectedRaven;
        var isSelectedRaven = selRaven && selRaven.ravenUuid === nextRaven.uuid;

        // Check if this raven is currently visible on the ImageGrid page
        const isOnCurrentPage = this.visibleRavenUuids.has(nextRaven.uuid);

        // Only request previews if it's the SnapshotPage AND it's currently on the screen
        let requestPreviews = (this.dfastMode === 'snapshot' && isOnCurrentPage) ? true : isSelectedRaven;

        console.log(`[DFAST] Raven: ${nextRaven.uuid} | OnPage: ${isOnCurrentPage} | Previews: ${requestPreviews}`);

        nextRaven.lastdfast = Date.now();
        this.ravenUuids.push(nextRaven);
        this.sendDfast(nextRaven.uuid, requestPreviews);
    }

    sendGetDistanceUnitsRequest() {

        if (!this.mqttClient) return;
        if (this.ending) return;
        var payload = {
            ctrlId: this.nextCtrlId(),
            queryRequest: [0]
        };

        var err = proto_CTRLRequest.verify(payload);
        if (err) throw Error(err);
        // Create a new message
        var message = proto_CTRLRequest.create(payload); // or use .fromObject if conversion is necessary
        // Encode a message to an Uint8Array (browser) or Buffer (node)
        var buffer = proto_CTRLRequest.encode(message).finish();
        const ravens = this.sessionManager.state.ravens.filter(r => !r._noMqtt);
        ravens.forEach(raven => {
            // Don't request units if we already have them
            if (raven.distanceUnits)
                return;

            const usersRavenCtrlTopic = this.ctrlTopic(raven.ravenUuid);
            if (!usersRavenCtrlTopic) {
                console.warn("MqttService sendGetDistanceUnitsRequest user ctrlTopic for raven is undefined.");
                return;
            }

            // console.log(`MQTTService requesting units from Raven ${raven.ravenUuid} (ctrlId=${payload.ctrlId})`);

            // this.mqttClient.publish('things/' + raven.ravenUuid + '/' + this.sessionManager.state.user.stage + '/ctrl', buffer);
            this.mqttClient.publish(usersRavenCtrlTopic, buffer);
        });
    }

    sendConfigurationQuery(ravenUuid) {
        return this.sendCtrlRequest(ravenUuid, { queryRequest: [0] });
    }

    sendConfigurationRequest({ ravenUuid, request }) {
        return this.sendCtrlRequest(ravenUuid, { configurationRequest: request });
    }

    sendMediaRequest({ ravenUuid, request }) {

        if (!request) {
            return Promise.reject(Error("MqttService sendMediaRequest with invalid request."));
        }
        var eventId;
        if (request.eventId) {

            let eventUnixTimestamp;

            if (typeof request.eventId.timestamp === "string") {

                let eventDate = RavenUtil.toDate(request.eventId.timestamp);

                if (eventDate) {
                    eventUnixTimestamp = Math.round(eventDate.getTime() / 1000);
                }
            } else if (typeof request.eventId.timestamp === "object") {
                eventUnixTimestamp = Math.round(request.eventId.timestamp.getTime() / 1000);
            } else {
                eventUnixTimestamp = request.eventId.timestamp;
            }

            if (!eventUnixTimestamp || !request.eventId.type) {
                return Promise.reject(Error("MqttService sendMediaRequest missing EventId."));
            }

            eventId = {
                timestamp: Number(eventUnixTimestamp),
                type: this.eventTypeEnum.values[request.eventId.type]
            };
        }

        if (!eventId) {
            console.warn("MqttService sendMediaRequest missing EventId. Assuming legacy request for sourceMediaId " + request.sourceMediaId);

            const mediaRequest = { // DEPRECATED_VIDEO_PREVIEW_REQUEST
                sourceMediaId: Number(request.sourceMediaId) || undefined,
                cameraType: this.cameraTypeEnum.values[request.cameraType],
                mediaType: this.mediaTypeEnum.values[request.mediaType],
            };
            console.log('sendMediaRequest', { ravenUuid, request, mediaRequest });
            return this.sendCtrlRequest(ravenUuid, { mediaRequest });
        }

        const mediaRequest = {
            sourceMediaId: Number(request.sourceMediaId) || undefined,  // DEPRECATED_VIDEO_PREVIEW_REQUEST
            duration: Number(request.duration) || undefined,
            cameraType: this.cameraTypeEnum.values[request.cameraType], // RAV-1041 - request for botch cameras ()
            mediaType: this.mediaTypeEnum.values[request.mediaType],
            eventId: eventId || undefined,
            startOffset: Number(request.startOffset) || undefined
        };
        console.log('sendMediaRequest', { ravenUuid, request, mediaRequest });
        return this.sendCtrlRequest(ravenUuid, { mediaRequest });
    }

    removeCtrlRequestInProgress(ctrlId, cancelTimeout = true) {
        const index = this.ctrlRequestsInProgress.findIndex(it => it.ctrlId === ctrlId);
        if (index === -1) return null;

        console.log(`index=${index}`);
        const removed = this.ctrlRequestsInProgress.splice(index, 1)[0];
        if (cancelTimeout && removed.timeoutId) {
            console.log(`clearing timeout for ${ctrlId}`);
            clearTimeout(removed.timeoutId);
        }
        return removed;
    }

    addCtrlRequestInProgress(ctrlId, resolve, reject, timeoutInMs = 30000) {
        /* set a 30s timeout */
        const timeoutId = setTimeout(() => {
            this.removeCtrlRequestInProgress(ctrlId, false);
            reject(Error("timeout"));
        }, timeoutInMs);
        this.ctrlRequestsInProgress.push({ ctrlId, resolve, reject, timeoutId });
        // console.log(JSON.stringify(this.ctrlRequestsInProgress));
    }

    ctrlTopic(ravenUuid) {
        if (!this.userid) {
            console.warn("MqttService ctrlTopic userid is not set.");
            return;
        }
        if (!this.sessionManager.state.user || !this.sessionManager.state.user.stage) {
            console.warn("MqttService ctrlTopic sessionManager user state or stage is not set.");
            return;
        }
        return `things/${this.userid}/${ravenUuid}/${this.sessionManager.state.user.stage}/ctrl`; // <-- https://sentry.io/organizations/klashwerks/issues/2911925745/?environment=test%2Bqa%2Bbeta&project=1264372&query=is%3Aunresolved&statsPeriod=14d
    }

    sendCtrlRequest(ravenUuid, request) {
        if (!ravenUuid) ravenUuid = this.showcaseTargetRavenUuid;
        if (!ravenUuid) {
            return Promise.reject(Error("ravenUuid not provided"));
        }
        const usersRavenCtrlTopic = this.ctrlTopic(ravenUuid);
        if (!usersRavenCtrlTopic) {
            return Promise.reject(Error("User ctrlTopic for raven is undefined."));
        }

        const service = this;
        return new Promise(function (resolve, reject) {
            if (!service.mqttClient) throw Error("mqttService is uninitialized");
            if (service.ending) {
                throw Error("mqttService is ending");
            }
            const payload = {
                ctrlId: service.nextCtrlId(),
                ...request
            };
            const err = proto_CTRLRequest.verify(payload);
            if (err) {
                console.log(payload);
                throw Error(err);
            }
            const message = proto_CTRLRequest.create(payload);
            const buffer = proto_CTRLRequest.encode(message).finish();
            service.mqttClient.publish(usersRavenCtrlTopic, buffer);
            // console.log(`sent ctrl request ${payload.ctrlId} to ${ravenUuid}`);
            service.addCtrlRequestInProgress(payload.ctrlId, resolve, reject);
        });
    }

    onMqttConnectionChanged(connected) {

        try {
            console.log('MQTTService connected: ', connected);

            if (connected) {
                this.startInteractionTimeout();
                this.sendAllRavensDfast();
                this.sendGetDistanceUnitsRequest();
            }
        } catch (e) {
            console.log(e);
        }
    }

    onMQTTEvent(ravenUuid, event) {

        try {
            if (event && event.type) {
                var targetRaven = this.sessionManager.state.ravens.find(raven => { return raven.ravenUuid === ravenUuid });
                var db = targetRaven.dashboard;
                if (event.type.indexOf("ENGINE") > -1) {
                    document.dispatchEvent(new CustomEvent(Events.REFRESH_RAVEN, { detail: { ravenUuid: ravenUuid } }));
                    return false;
                }
                // when Settings is open, dashboard may be null or undefined
                if (!db) return;
                //reload Raven to show map properly if we start getting gps data.
                if (db.route && event.route) {
                    if (db.route.length <= 0 && event.route.length > 0) {
                        document.dispatchEvent(new CustomEvent(Events.REFRESH_RAVEN, { detail: { ravenUuid: ravenUuid } }));
                    }
                }
                //modify raven attributes based on incoming data
                event.ts *= 1000;
                event.ts = new Date(event.ts);
                const events = [...db.events, event]; // make a copy of events with the new event at the end
                const allSupportedEvents = [...db.allSupportedEvents, event]; //TODO remove by moving to the redux store
                if (event.type) {
                    // console.log('event.type', event.type);
                    if (event.type.indexOf('ENGINE_OFF') > -1) {
                        db.type = "PARKED";
                    } else if (event.type.indexOf('ENGINE_ON') > -1) {
                        db.type = "DRIVING";
                    }
                    if (event.type.indexOf('UNPLUGGED') > -1) {
                        targetRaven.unplugged = true;
                    } else if (event.type.indexOf('PLUGGED_IN') > -1 ||
                        event.type.indexOf('ENGINE_ON') > -1) {
                        targetRaven.unplugged = false;
                        targetRaven.shutdown = false;
                    } else if (event.type.indexOf('SHUTDOWN') > -1) {
                        targetRaven.unplugged = true;
                        targetRaven.shutdown = true;
                    } else if (event.type.indexOf('HARD_BRAKING') > -1) {

                        // Post a desktop notification if there is a hard braking event
                        var evt = new CustomEvent(Events.RAVEN_MQTT_NOTIFICATION, {
                            detail: {
                                event: event,
                                ravenUuid: ravenUuid,
                                type: p_event_type[event.type]
                            }
                        });

                        this.notificationService.makeNotification(evt);
                    }
                }
                db.allSupportedEvents = JSON.parse(JSON.stringify(allSupportedEvents)); //TODO remove by moving to the redux store (JSON is being used as a deep copy solution)
                db.events = RavenUtil.sortEvents(events); // assign the events array copy to the dashboard
                return true;
            }
        } catch (err) {
            console.error(err);

            return false;
        }
    }

    onMQTTRavenUnplugged(ravenUuid, unplugged) {
        try {
            var targetRaven = this.sessionManager.state.ravens.find(raven => { return raven.ravenUuid === ravenUuid });
            if (!targetRaven || !targetRaven.dashboard) return;
            targetRaven.unplugged = unplugged
        } catch (err) {
            console.error(err);
            return false;
        }
    }

    onMQTTRavenIdlingUpdate(ravenUuid, isIdling) {

        try {
            var targetRaven = this.sessionManager.state.ravens.find(raven => { return raven.ravenUuid === ravenUuid });
            if (!targetRaven || !targetRaven.dashboard) return;
            targetRaven.isIdling = isIdling
        } catch (err) {
            console.error(err);
            return false;
        }
    }

    onMQTTSDCardStatusUpdate(ravenUuid, status) {

        try {
            var targetRaven = this.sessionManager.state.ravens.find(raven => { return raven.ravenUuid === ravenUuid });
            if (!targetRaven || !targetRaven.dashboard) return;
            console.log("MQTT SERVICE onMQTTSDCardStatusUpdate targetRaven.sdCardStatus: " + targetRaven.sdCardStatus + ". Updating to: " + status);
            targetRaven.sdCardStatus = status
        } catch (err) {
            console.error(err);
            return false;
        }
    }

    onMQTTRouteUpdate(ravenUuid, routeUpdate) {

        try {
            var targetRaven = this.sessionManager.state.ravens.find(raven => { return raven.ravenUuid === ravenUuid });
            if (!targetRaven || !targetRaven.dashboard) return;

            const mostRecentLocationUpdate = routeUpdate.last();
            const targetRavensLastLocation = targetRaven.lastLocation;

            if (targetRavensLastLocation && mostRecentLocationUpdate.ts <= targetRavensLastLocation.ts) {
                return false; // ignore stale data, do not trigger a refresh (updateRavens not set to true)
            }

            targetRaven.dashboard.route = targetRaven.dashboard.route.concat(routeUpdate);
            targetRaven = RavenUtil.updateHeading(targetRaven); //updates raven ref with new heading
            targetRaven.lastLocation = {
                ...mostRecentLocationUpdate,
                ts: RavenUtil.toDate(mostRecentLocationUpdate.ts)
            }
            return true;
        } catch (err) {
            console.error(err);
            return false;
        }
    }

    onMQTTConfigResponse(ravenUuid, configurationResponse) {

        try {
            if (!configurationResponse) return;
            var targetRaven = this.sessionManager.state.ravens.find(raven => { return raven.ravenUuid === ravenUuid });
            if (!targetRaven) return;
            const displayConfiguration = configurationResponse.displayConfiguration;
            if (displayConfiguration) {
                targetRaven.distanceUnits = displayConfiguration.distanceUnits;
            }
        } catch (err) {
            console.error(err);
        }
    }

    onMQTTTelematicsUpdate(ravenUuid, currentFuel, currentSpeed, ts) {

        try {
            var targetRaven = this.sessionManager.state.ravens.find(raven => { return raven.ravenUuid === ravenUuid });
            if (!targetRaven || !targetRaven.dashboard) return false;
            var vs = targetRaven.vehicleState ? targetRaven.vehicleState : {};
            if (currentFuel) vs.currentFuel = currentFuel;
            if (currentSpeed) vs.currentSpeed = currentSpeed;
            return true;
        } catch (err) {
            console.error(err);
            return false;
        }
    }

    onMQTTLiveImage(ravenUuid, media) {

        if (media[0]) {
            try {
                var targetRaven = this.sessionManager.state.ravens.find(raven => { return raven.ravenUuid === ravenUuid });
                if (!targetRaven || !targetRaven.dashboard) return false;
                if (!targetRaven.liveMedia) targetRaven.liveMedia = [{}, {}];

                if (media[0].imageData && media[0].imageData.length > 0) {
                    media[0].base64Image = "data:image/png;base64," + this.arrayBufferToBase64(media[0].imageData);
                    media[0].timestamp *= 1000; // add 3 zeros
                    switch (media[0].cameraType) {
                        case 1:
                            targetRaven.liveMedia[0] = media[0]; //road camera
                            break;
                        case 2:
                            targetRaven.liveMedia[1] = media[0]; //cabin camera
                            break;
                    }
                    return true;
                }
            } catch (e) {
                console.error(e);
                return false;
            }
        }
        return false;
    }

    arrayBufferToBase64(buffer) {
        var binary = '';
        var bytes = new Uint8Array(buffer);
        var len = bytes.byteLength;
        for (var i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }

    updateActiveRavens(activeRavenUuids) {
        if (this.dfastTimeout) clearTimeout(this.dfastTimeout);

        // 1. If we provide a list, use it. 
        // 2. If the list is empty/null, revert to ALL ravens from sessionManager.
        const targetList = (activeRavenUuids && activeRavenUuids.length > 0)
            ? activeRavenUuids
            : this.sessionManager.state.ravens
                .filter(r => !r.noServicePlan && !r._noMqtt)
                .map(r => r.ravenUuid);

        this.ravenUuids = targetList.map(uuid => ({
            uuid: uuid,
            lastdfast: Date.now() - this.dfastInterval
        }));

        if (this.dfastEnabled && !this.dfastPaused && this.ravenUuids.length > 0) {
            this.sendAllRavensDfast();
        }
    }

}
